import { apiClient, unwrap } from '@services/api';
import type {
  AdminRestaurantSubscription,
  AdminSubscriptionPlan,
  BillingSettings,
} from '@/types';

export const adminBillingService = {
  async getPlans(): Promise<AdminSubscriptionPlan[]> {
    const res = await apiClient.get<AdminSubscriptionPlan[]>('/admin/billing/plans');
    return unwrap(res);
  },

  async createPlan(data: {
    name: string;
    description?: string;
    price: number;
    isActive?: boolean;
    sortOrder?: number;
  }): Promise<AdminSubscriptionPlan> {
    const res = await apiClient.post<AdminSubscriptionPlan>('/admin/billing/plans', data);
    return unwrap(res);
  },

  async updatePlan(
    id: string,
    data: Partial<{
      name: string;
      description: string;
      price: number;
      isActive: boolean;
      sortOrder: number;
    }>,
  ): Promise<AdminSubscriptionPlan> {
    const res = await apiClient.patch<AdminSubscriptionPlan>(`/admin/billing/plans/${id}`, data);
    return unwrap(res);
  },

  async deletePlan(id: string): Promise<{ deleted: boolean; deactivated?: boolean }> {
    const res = await apiClient.delete<{ deleted: boolean; deactivated?: boolean }>(
      `/admin/billing/plans/${id}`,
    );
    return unwrap(res);
  },

  async getSettings(): Promise<BillingSettings> {
    const res = await apiClient.get<BillingSettings>('/admin/billing/settings');
    return unwrap(res);
  },

  async setTrialDays(trialDays: number): Promise<{ trialDays: number }> {
    const res = await apiClient.post<{ trialDays: number }>('/admin/billing/settings/trial-days', {
      trialDays,
    });
    return unwrap(res);
  },

  async setEnforcement(enforced: boolean): Promise<{ enforced: boolean }> {
    const res = await apiClient.post<{ enforced: boolean }>('/admin/billing/settings/enforcement', {
      enforced,
    });
    return unwrap(res);
  },

  async getSubscriptions(): Promise<AdminRestaurantSubscription[]> {
    const res = await apiClient.get<AdminRestaurantSubscription[]>('/admin/billing/subscriptions');
    return unwrap(res);
  },

  async extendTrial(
    restaurantId: string,
    trialEndsAt: string,
  ): Promise<{ restaurantId: string; trialEndsAt: string }> {
    const res = await apiClient.post<{ restaurantId: string; trialEndsAt: string }>(
      `/admin/billing/restaurants/${restaurantId}/trial`,
      { trialEndsAt },
    );
    return unwrap(res);
  },

  async syncNow(): Promise<{ synced: number }> {
    const res = await apiClient.post<{ synced: number }>('/admin/billing/sync');
    return unwrap(res);
  },
};
